import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { motion } from "framer-motion";
import { Loader2, Zap, Users, Mail } from "lucide-react"; // MessageSquare removed for Facebook review
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { MoujibLogo } from "../components/ui/logo";
import {
  auth0Config,
  facebookLoginOptions,
  googleLoginOptions,
  emailLoginOptions,
  emailSignupOptions,
} from "../config/auth0";
import { useApi } from "../lib/api";
import { resolveLandingAndSeed } from "../lib/landing";

// Auth0 Database connection used for email/password accounts. Must match the
// connection named in emailLoginOptions / emailSignupOptions (config/auth0.ts).
const EMAIL_PASSWORD_CONNECTION = "Username-Password-Authentication";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, isLoading, user, error } =
    useAuth0();
  const [authError, setAuthError] = useState("");
  const api = useApi();
  const redirectedRef = useRef(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");

  // Session expiration removed - no longer needed

  // Already signed in (live Auth0 session): send them where they belong using
  // the exact same server-side decision as /callback, so the two entry points
  // can't drift apart. `replace` keeps /login out of history — a push would let
  // Back drop the user straight back into this redirect.
  useEffect(() => {
    if (!isAuthenticated || !user || redirectedRef.current) return;
    redirectedRef.current = true;

    (async () => {
      const { target, requiresSignup } = await resolveLandingAndSeed(api);
      const destination = requiresSignup
        ? `/signup?redirect=${encodeURIComponent(target)}`
        : target;
      console.log("✅ [LOGIN] Already authenticated, redirecting to", destination);
      navigate(destination, { replace: true });
    })();
    // 'api' omitted from deps on purpose (useApi returns a new object each
    // render) — same reason as ProtectedRoute; redirectedRef guards re-entry.
  }, [isAuthenticated, user, navigate]);

  // One handler for every Auth0 connection — pass the provider's login options
  // and a label used only for the error message.
  const startLogin = async (
    label: string,
    options: Parameters<typeof loginWithRedirect>[0]
  ) => {
    try {
      setAuthError("");
      await loginWithRedirect(options);
    } catch (err) {
      console.error(`${label} login error:`, err);
      setAuthError(`${label} login failed. Please try again.`);
    }
  };

  const handleFacebookLogin = () =>
    startLogin("Facebook", facebookLoginOptions);
  const handleGoogleLogin = () => startLogin("Google", googleLoginOptions);
  const handleEmailLogin = () => startLogin("Email", emailLoginOptions);
  const handleEmailSignup = () => startLogin("Email", emailSignupOptions);

  // Trigger Auth0's hosted password-reset email for the Database connection.
  // Auth0 always responds 200 with a generic message (no email enumeration), so
  // a successful POST means "if that account exists, a reset email was sent".
  const handlePasswordReset = async () => {
    const email = resetEmail.trim();
    if (!email) return;
    setResetStatus("sending");
    try {
      const res = await fetch(
        `https://${auth0Config.domain}/dbconnections/change_password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            client_id: auth0Config.clientId,
            email,
            connection: EMAIL_PASSWORD_CONNECTION,
          }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      setResetStatus("sent");
    } catch (err) {
      console.error("Password reset error:", err);
      setResetStatus("error");
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        {/* Main Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="text-center pb-8 pt-12">
              {/* Logo and Branding */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                className="mx-auto mb-6"
              >
                <MoujibLogo size="xxxl" variant="color" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                  Welcome to Moujib
                </CardTitle>
                <CardDescription className="text-gray-600 text-lg">
                  Smart Facebook Page Management
                </CardDescription>
              </motion.div>
            </CardHeader>

            <CardContent className="px-8 pb-12">
              {/* Features Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-8 space-y-4"
              >
                {/* COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation */}
                {/* <div className="flex items-center space-x-3 text-gray-700">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm">
                    Auto-reply to messages in Derja, Arabic & French
                  </span>
                </div> */}
                <div className="flex items-center space-x-3 text-gray-700">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Zap className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm">Smart comment moderation</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-700">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm">
                    Manage multiple Facebook pages
                  </span>
                </div>
              </motion.div>

              {/* Session expiration messages removed */}

              {/* Error Display */}
              {(error || authError) && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
                >
                  {error?.message || authError}
                </motion.div>
              )}

              {/* Facebook Login Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <Button
                  onClick={handleFacebookLogin}
                  className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white flex items-center justify-center py-6 text-lg font-semibold shadow-lg transition-all duration-200 hover:shadow-xl transform hover:scale-[1.02]"
                  size="lg"
                >
                  <svg
                    className="h-6 w-6 mr-3"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Continue with Facebook
                </Button>

                {/* Continue with Google */}
                <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full mt-3 bg-white text-gray-700 border-gray-300 hover:bg-gray-50 flex items-center justify-center py-6 text-lg font-semibold shadow-sm transition-all duration-200 hover:shadow"
                  size="lg"
                >
                  <svg className="h-6 w-6 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </Button>

                {/* Divider */}
                <div className="flex items-center my-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="px-3 text-xs text-gray-400 uppercase tracking-wide">
                    or
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Email + password */}
                <Button
                  onClick={handleEmailLogin}
                  variant="outline"
                  className="w-full flex items-center justify-center py-6 text-base font-semibold border-gray-300 text-gray-700 hover:bg-gray-50"
                  size="lg"
                >
                  <Mail className="h-5 w-5 mr-2" />
                  Continue with email
                </Button>

                <p className="mt-4 text-center text-sm text-gray-600">
                  New to Moujib?{" "}
                  <button
                    type="button"
                    onClick={handleEmailSignup}
                    className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2"
                  >
                    Create an account
                  </button>
                </p>

                {/* Forgot password — Auth0 hosted reset for email/password accounts */}
                <div className="mt-2 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setResetOpen((v) => !v);
                      setResetStatus("idle");
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
                  >
                    Forgot password?
                  </button>
                </div>

                {resetOpen && (
                  <div className="mt-3 space-y-2">
                    {resetStatus === "sent" ? (
                      <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-center">
                        If an account exists for that email, a password reset
                        link is on its way. Check your inbox.
                      </p>
                    ) : (
                      <>
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => {
                            setResetEmail(e.target.value);
                            if (resetStatus === "error") setResetStatus("idle");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handlePasswordReset();
                          }}
                          placeholder="you@example.com"
                          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        {resetStatus === "error" && (
                          <p className="text-xs text-red-600">
                            Couldn't send the reset email. Please try again.
                          </p>
                        )}
                        <Button
                          type="button"
                          onClick={handlePasswordReset}
                          disabled={
                            !resetEmail.trim() || resetStatus === "sending"
                          }
                          variant="outline"
                          className="w-full flex items-center justify-center py-5 text-sm font-semibold border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                          {resetStatus === "sending" ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Send reset link"
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </motion.div>

              {/* Info Section */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-8 text-center"
              >
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-sm text-blue-800 font-medium mb-2">
                    🔒 Secure Facebook Integration
                  </p>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Connect your Facebook pages securely to start managing
                    comments and messages with AI assistance. Your data is
                    protected and never shared.
                  </p>
                </div>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="text-center mt-8"
        >
          <p className="text-gray-600 text-sm">
            Made for Tunisian businesses 🇹🇳
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
